import React, { useEffect, useState } from "react";
import { adminGet, adminPost } from "../common/api/api";

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ productName: "", userName: "", rating: 5, comment: "" });

  const fetchReviews = async () => {
    setLoading(true);
    const data = await adminGet("/allreviews");
    setReviews(Array.isArray(data) ? data : []);
    setLoading(false);
  };

  useEffect(() => { fetchReviews(); }, []);

  const approveReview = async (id) => {
    await adminPost("/approvereview", { id });
    fetchReviews();
  };

  const deleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await adminPost("/deletereview", { id });
    fetchReviews();
  };

  const addReview = async () => {
    if (!form.productName || !form.userName || !form.comment) { alert("All fields are required"); return; }
    await adminPost("/addreview", { ...form, rating: parseInt(form.rating) });
    setForm({ productName: "", userName: "", rating: 5, comment: "" });
    setShowForm(false);
    fetchReviews();
  };

  const filtered =
    filter === "all" ? reviews :
    filter === "approved" ? reviews.filter(r => r.approved) :
    reviews.filter(r => !r.approved);

  const stars = (n) => "★".repeat(n) + "☆".repeat(5 - n);

  const inputClass = "bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[#e2e8f0] placeholder-[#4a5568] focus:outline-none focus:border-[#00d4ff]/60 text-sm w-full";

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Reviews</h1>
          <p className="text-sm text-[#4a5568] mt-0.5">{reviews.filter(r => !r.approved).length} pending approval</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-gradient-to-r from-[#00d4ff] to-[#0099cc] text-[#060c18] font-bold rounded-lg text-sm hover:opacity-90 transition-opacity cursor-pointer"
        >
          + Add Review
        </button>
      </div>

      {/* Add Review Form */}
      {showForm && (
        <div className="mb-5 p-4 bg-white/[0.03] border border-white/[0.08] rounded-xl">
          <h3 className="text-sm font-semibold text-white mb-3">New Review</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input className={inputClass} placeholder="Product Name *" value={form.productName} onChange={e => setForm({ ...form, productName: e.target.value })} />
            <input className={inputClass} placeholder="Customer Name *" value={form.userName} onChange={e => setForm({ ...form, userName: e.target.value })} />
            <select className={inputClass} style={{ background: '#0d1527' }} value={form.rating} onChange={e => setForm({ ...form, rating: e.target.value })}>
              {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} Star{n !== 1 ? "s" : ""}</option>)}
            </select>
            <input className={inputClass} placeholder="Comment *" value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })} />
          </div>
          <div className="flex gap-2">
            <button onClick={addReview} className="px-4 py-2 bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border border-[rgba(0,212,255,0.3)] rounded-lg text-sm hover:bg-[rgba(0,212,255,0.25)] transition-colors cursor-pointer">Save</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-[#4a5568] border border-white/[0.08] rounded-lg text-sm hover:border-white/[0.2] transition-colors cursor-pointer">Cancel</button>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { key: "all", label: `All (${reviews.length})` },
          { key: "pending", label: `Pending (${reviews.filter(r => !r.approved).length})` },
          { key: "approved", label: `Approved (${reviews.filter(r => r.approved).length})` },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setFilter(key)}
            className={`px-3 py-1 rounded-full text-xs font-medium border transition-all cursor-pointer ${
              filter === key ? "bg-[rgba(0,212,255,0.15)] text-[#00d4ff] border-[rgba(0,212,255,0.4)]" : "text-[#4a5568] border-white/[0.08] hover:border-white/[0.2] hover:text-[#a0aec0]"
            }`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#4a5568] py-16 text-center">Loading reviews...</div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-[#4a5568]">No reviews found.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((review) => (
            <div key={review._id} className="p-4 bg-white/[0.02] border border-white/[0.07] rounded-xl">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[#00d4ff] font-medium text-sm">{review.productName}</span>
                    <span className="text-yellow-400 text-sm">{stars(review.rating || 5)}</span>
                    {review.approved ? (
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-green-400/10 text-green-400 border border-green-400/30 font-bold">APPROVED</span>
                    ) : (
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 font-bold">PENDING</span>
                    )}
                  </div>
                  <p className="text-[#e2e8f0] text-sm mb-1">"{review.comment}"</p>
                  <p className="text-[#4a5568] text-xs">— {review.userName} · {review.date ? new Date(review.date).toLocaleDateString() : ""}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => approveReview(review._id)}
                    className={`text-xs px-3 py-1.5 rounded-lg border transition-all cursor-pointer ${
                      review.approved
                        ? "text-[#4a5568] border-white/[0.08] hover:border-white/[0.2]"
                        : "text-green-400 border-green-400/30 bg-green-400/5 hover:bg-green-400/15"
                    }`}
                  >
                    {review.approved ? "Unapprove" : "Approve"}
                  </button>
                  <button
                    onClick={() => deleteReview(review._id)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-all cursor-pointer"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
